import clsx from 'clsx';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

import { CloseIcon } from '~components/icons/CloseIcon';
import { MagnifyingGlassIcon } from '~components/icons/MagnifyingGlassIcon';

type InputSearchProps = {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onEnter?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export function InputSearch({ value, placeholder, onChange, onClear, onEnter, onFocus, onBlur }: InputSearchProps) {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const refInput = useRef<HTMLInputElement>(null);

  // Handler: toggle focus and callback
  const toggleFocus = useCallback(
    (focus: boolean) => {
      setHasFocus(focus);
      if (focus) {
        onFocus?.();
      }
      if (!focus) {
        onBlur?.();
      }
    },
    [onBlur, onFocus],
  );

  // Handler: external onChange callback
  const handleOnChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      onChange?.(evt.currentTarget.value);
    },
    [onChange],
  );

  // Handler: external onClear callback
  const handleOnClear = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      onClear?.();
    },
    [onClear],
  );

  // Handler: internal onClick callback triggers focus of input tag when element is clicked outside of input
  const handleClick = useCallback(() => {
    if (!refInput.current) return;
    if (refInput.current === document.activeElement) return;
    refInput.current.focus();
  }, []);

  // Handler: key press to capture enter
  const handleKeyPress = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Enter') {
        onEnter?.();
      }
    },
    [onEnter],
  );

  return (
    <div
      className={clsx(
        'ml--input-search',
        'flex border-solid border p-2 rounded bg-white items-center overflow-hidden cursor-text justify-between',
        hasFocus ? 'border-black' : 'border-silver hover:border-black',
        hasFocus && 'has-focus',
      )}
      onClick={handleClick}
    >
      <div className="ml--input-search--icon pointer-events-none flex items-center">
        <MagnifyingGlassIcon className="w-7 h-7 text-black" />
      </div>
      <input
        ref={refInput}
        onChange={handleOnChange}
        className="ml--input-search--input appearance-none border-none outline-none p-0 mx-2 h-8 font-sans text-lg leading-none text-black flex-grow"
        defaultValue={value}
        placeholder={placeholder}
        onFocus={() => toggleFocus(true)}
        onBlur={() => toggleFocus(false)}
        onKeyPress={handleKeyPress}
      />
      <button
        className="ml--input-seach--clear appearance-none p-0 -mr-1 border-none outline-none bg-transparent cursor-pointer text-darkgray hover:text-black"
        onClick={handleOnClear}
      >
        <CloseIcon className="w-7 h-7 text-current" />
      </button>
    </div>
  );
}
